import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { User } from "@supabase/supabase-js";

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let requestId = 0;
    let resolvedUserId: string | null = null;
    let resolvedIsAdmin = false;
    let resolvedIsBlocked = false;

    const checkAdmin = async (userId: string) => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("[useAuth] role check failed:", error);
        return false;
      }

      return !!data;
    };

    const checkBlocked = async (userId: string) => {
      const { data, error } = await (supabase as any)
        .from("profiles")
        .select("blocked_at")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) {
        console.error("[useAuth] blocked check failed:", error);
        return false;
      }
      return !!data?.blocked_at;
    };

    const applyAuthState = (nextUser: User | null) => {
      const currentRequestId = ++requestId;
      setUser(nextUser);

      if (!nextUser) {
        resolvedUserId = null;
        resolvedIsAdmin = false;
        resolvedIsBlocked = false;
        setIsAdmin(false);
        setIsBlocked(false);
        setLoading(false);
        return;
      }

      if (resolvedUserId === nextUser.id) {
        setIsAdmin(resolvedIsAdmin);
        setIsBlocked(resolvedIsBlocked);
        setLoading(false);
        return;
      }

      setLoading(true);

      Promise.all([checkAdmin(nextUser.id), checkBlocked(nextUser.id)]).then(([nextIsAdmin, nextIsBlocked]) => {
        if (cancelled || currentRequestId !== requestId) return;
        resolvedUserId = nextUser.id;
        resolvedIsAdmin = nextIsAdmin;
        resolvedIsBlocked = nextIsBlocked;
        setIsAdmin(nextIsAdmin);
        setIsBlocked(nextIsBlocked);
        setLoading(false);

        // Auto sign-out non-admin blocked users
        if (nextIsBlocked && !nextIsAdmin) {
          supabase.auth.signOut().then(() => {
            toast.error("Twoje konto zostało zablokowane. Skontaktuj się z obsługą sklepu.");
          });
        }
      });
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (cancelled) return;

        const nextUser = session?.user ?? null;

        if (event === "TOKEN_REFRESHED" && nextUser?.id && resolvedUserId === nextUser.id) {
          setUser(nextUser);
          setIsAdmin(resolvedIsAdmin);
          setIsBlocked(resolvedIsBlocked);
          setLoading(false);
          return;
        }

        applyAuthState(session?.user ?? null);
      }
    );

    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (cancelled) return;
      if (error) {
        console.error("[useAuth] getSession failed:", error);
        setUser(null);
        setIsAdmin(false);
        setIsBlocked(false);
        setLoading(false);
        return;
      }

      applyAuthState(session?.user ?? null);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = () => supabase.auth.signOut();

  return { user, isAdmin, isBlocked, loading, signOut };
}

